import { Int } from '@adornis/baseql/baseqlTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import { validate } from '@adornis/validation/decorators.js';
import { nonOptional } from '@adornis/validation/functions/nonOptional.js';
import type { MailProcessingEvaluation } from './MailExtractionResult.js';

@Entity()
export class MonthOfYear extends AdornisEntity {
  static override _class = 'MonthOfYear';

  @validate(nonOptional())
  @Field(() => Int)
  month!: number;

  @validate(nonOptional())
  @Field(() => Int)
  year!: number;

  static createByDate(date: Date) {
    return new this({
      month: date.getMonth(),
      year: date.getFullYear(),
    });
  }

  static create({ month, year }: { month: number; year: number }) {
    return new this({
      month,
      year,
    });
  }
}

@Entity()
export class MailProcessingFilter extends AdornisFilter {
  static override _class = 'MailProcessingFilter';

  @Field(() => MonthOfYear)
  monthOfYear?: MonthOfYear;

  @Field(() => String)
  evaluation?: MailProcessingEvaluation;

  // Scope statistics to a single sender (the H&D employee who forwarded the
  // request). `mineOnly` is the client intent; the server resolves it into
  // `senderAddress`. Sender lives on the joined `mail` document, so it is
  // applied as a separate pipeline stage by the stats queries, not here.
  @Field(() => Boolean)
  mineOnly?: boolean;

  @Field(() => String)
  senderAddress?: string;

  /**
   * Just the selected period, as a `createdAt` window. Split out because the notification
   * statistics count over a collection that has no `evaluation` at all, but must land in the
   * same month as the tiles above them.
   */
  getMongoDateFilter() {
    if (!this.monthOfYear) return {};
    return {
      createdAt: {
        $gte: new Date(this.monthOfYear.year, this.monthOfYear.month - 1, 1),
        $lt: new Date(this.monthOfYear.year, this.monthOfYear.month, 1),
      },
    };
  }

  getMongoMatchFilter() {
    const filter = {
      ...this.getMongoDateFilter(),
      ...(this.evaluation ? { evaluation: this.evaluation } : {}),
    };
    return filter;
  }
}
